import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import TopBar from '../../layouts/TopBar/topBar';
import { Spinner } from '../../ui/atoms';
import { formatDisplayDateTime } from '../../utils/dateFormatting';
import { describePodcastError, getPodcast } from './podcastApi';
import {
  FALLBACK_CONFIG,
  POLL_INTERVAL_MS,
  formatDuration,
  getJobTitle,
  getSourceType,
  isJobActive
} from './podcastJob';
import { languageLabel, toneLabel } from './podcastLabels';
import usePodcastConfig from './usePodcastConfig';
import PodcastProgress from './PodcastProgress';
import PodcastResult from './PodcastResult';
import { StatusBadge } from './PodcastStatus';
import { PODCASTS_PATH } from './podcastJob';

const Card = ({ title, children, ...rest }) => (
  <section className="rounded-xl border border-neutral-200 bg-white p-6" {...rest}>
    {title && <h2 className="text-base font-medium text-gray-900 mb-4">{title}</h2>}
    {children}
  </section>
);

const Param = ({ label, children }) => (
  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 break-words">{children}</dd>
  </div>
);

const PodcastParams = ({ job, hostName }) => {
  const { t, i18n } = useTranslation();
  const type = getSourceType(job);
  const target = formatDuration(job.seconds);
  const actual = formatDuration(job.durationSeconds);

  return (
    <dl className="divide-y divide-gray-100" data-testid="podcast-params">
      <Param label={t('podcasts.detail.params.source')}>
        <span className="text-gray-500">{t(`podcasts.sourceTypes.${type}`)}: </span>
        {type === 'url' && job.url ? (
          <a href={job.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 break-all">
            {job.sourceTitle || job.url}
          </a>
        ) : (
          <span>{job.sourceName || '-'}</span>
        )}
      </Param>
      <Param label={t('podcasts.detail.params.language')}>{languageLabel(job.language) || '-'}</Param>
      <Param label={t('podcasts.detail.params.tone')}>{toneLabel(job, t) || '-'}</Param>
      <Param label={t('podcasts.detail.params.hosts')}>
        {job.hostA || job.hostB ? (
          <>
            {t('podcasts.speakers.A')}: {hostName(job.hostA) || '-'}
            <br />
            {t('podcasts.speakers.B')}: {hostName(job.hostB) || '-'}
          </>
        ) : '-'}
      </Param>
      <Param label={t('podcasts.detail.params.length')}>
        {target ? t('podcasts.detail.targetLength', { value: target }) : '-'}
        {actual ? ` · ${t('podcasts.detail.actualLength', { value: actual })}` : null}
      </Param>
      <Param label={t('common.creationDate')}>
        {job.createdAt ? formatDisplayDateTime(job.createdAt, i18n.language) : '-'}
      </Param>
      {job.finishedAt && (
        <Param label={t('podcasts.detail.params.finishedAt')}>
          {formatDisplayDateTime(job.finishedAt, i18n.language)}
        </Param>
      )}
    </dl>
  );
};

const PodcastError = ({ job }) => {
  const { t } = useTranslation();
  const detail = job?.debug?.errorDetail;
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="podcast-error">
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">{t('podcasts.errors.failedTitle')}</p>
          <p className="mt-1">{job.error || t('podcasts.errors.generic')}</p>
          {detail && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-medium">{t('podcasts.detail.errorDetail')}</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-red-800" data-testid="podcast-error-detail">
                {typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

const PodcastDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { config } = usePodcastConfig();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pollError, setPollError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const goBack = useCallback(() => navigate(PODCASTS_PATH), [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    try {
      const data = await getPodcast(id);
      if (mountedRef.current) setJob(data);
    } catch (error) {
      if (!mountedRef.current) return;
      if (error.status === 404) setNotFound(true);
      else setLoadError(describePodcastError(error, t));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    setJob(null);
    load();
  }, [load]);

  const active = isJobActive(job);

  // Odpytywanie co 2 s, dopóki zadanie jest aktywne.
  useEffect(() => {
    if (!active) return undefined;
    let inFlight = false;
    const intervalId = window.setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await getPodcast(id);
        if (!mountedRef.current) return;
        setJob(data);
        setPollError(null);
      } catch (error) {
        if (!mountedRef.current) return;
        if (error.status === 404) setNotFound(true);
        else setPollError(describePodcastError(error, t));
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [active, id, t]);

  const hosts = useMemo(() => (config?.hosts || FALLBACK_CONFIG.hosts), [config]);
  const hostName = useCallback(
    (hostId) => (hostId ? (hosts.find((host) => host.id === hostId) || {}).name || hostId : null),
    [hosts]
  );
  const speakerNames = useMemo(() => {
    const names = {};
    if (job?.hostA) names.A = hostName(job.hostA);
    if (job?.hostB) names.B = hostName(job.hostB);
    return names;
  }, [job, hostName]);

  let body;
  if (loading && !job) {
    body = (
      <div className="flex justify-center py-24">
        <Spinner size="lg" label={t('common.loading', { defaultValue: 'Loading' })} />
      </div>
    );
  } else if (notFound) {
    body = (
      <Card>
        <div className="py-10 text-center" data-testid="podcast-not-found">
          <p className="text-lg font-medium text-gray-900">{t('podcasts.detail.notFound')}</p>
          <p className="mt-1 text-sm text-gray-500">{t('podcasts.detail.notFoundDescription')}</p>
          <button type="button" className="btn btn-primary btn-md mt-6" onClick={goBack}>
            {t('podcasts.detail.backToList')}
          </button>
        </div>
      </Card>
    );
  } else if (loadError || !job) {
    body = (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center justify-between gap-3">
        <span>{loadError || t('podcasts.errors.generic')}</span>
        <button type="button" className="btn btn-secondary btn-xs" onClick={load}>
          {t('podcasts.list.retry')}
        </button>
      </div>
    );
  } else {
    body = (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {job.status === 'done' ? (
            <Card title={t('podcasts.detail.resultTitle')}>
              <PodcastResult job={job} speakerNames={speakerNames} />
            </Card>
          ) : (
            <Card title={t('podcasts.detail.progressTitle')}>
              {job.status === 'failed' && (
                <div className="mb-6">
                  <PodcastError job={job} />
                </div>
              )}
              <PodcastProgress job={job} />
              {pollError && active && (
                <p className="mt-3 text-sm text-yellow-700" role="status">
                  {t('podcasts.errors.pollFailed')} {pollError}
                </p>
              )}
              {active && <p className="mt-4 text-xs text-gray-500">{t('podcasts.detail.activeHint')}</p>}
            </Card>
          )}
        </div>
        <Card title={t('podcasts.detail.paramsTitle')}>
          <PodcastParams job={job} hostName={hostName} />
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-transparent min-h-full">
      <TopBar onGoBack={goBack} mode="back-only" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mt-6 mb-8 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-3xl font-medium leading-tight text-primary-900 break-words">
              {job ? getJobTitle(job) || t('podcasts.result.untitled') : t('podcasts.detail.title')}
            </h1>
            {job && job.title && job.sourceTitle && job.sourceTitle !== job.title && (
              <p className="mt-2 text-base text-primary-900 opacity-60">{job.sourceTitle}</p>
            )}
          </div>
          {job && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <StatusBadge status={job.status} />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`${PODCASTS_PATH}/create`)}>
                {t('podcasts.createNew')}
              </button>
            </div>
          )}
        </div>
        {body}
      </div>
    </div>
  );
};

export default PodcastDetailPage;
